import React, { useCallback, useEffect, useRef, useState } from 'react';
import { trackTelemetryEvent } from '../utils/anonymousTelemetry';
import './PromoVideoPip.css';

const PREVIEW_SRC = '/videos/rkube-promo-preview.mp4';
const FULL_SRC = '/videos/rkube-promo.mp4';
const POSTER_SRC = '/videos/rkube-promo-poster.jpg';
const HOVER_OPEN_DELAY_MS = 350;
const DISMISS_KEY = 'rkube_promo_pip_dismissed';
const REOPEN_GUARD_MS = 700;

function prefersReducedMotion() {
  return typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// Floating picture-in-picture promo on the home page. Hover (mouse) or tap
// expands it to a full-window player that always starts muted; sound comes on
// when the viewer moves the mouse over, or taps, the full-window video.
// Browsers can refuse unmuting without a prior click/tap, so a "Tap for sound"
// fallback is shown in that case.
function PromoVideoPip() {
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === '1'; } catch (e) { return false; }
  });
  const [expanded, setExpanded] = useState(false);
  const [needsTapForSound, setNeedsTapForSound] = useState(false);
  const hoverTimerRef = useRef(null);
  const fullVideoRef = useRef(null);
  const closedAtRef = useRef(0);
  const hoverArmedRef = useRef(true);
  const pipRef = useRef(null);
  const reducedMotion = prefersReducedMotion();

  const open = useCallback((trigger) => {
    window.clearTimeout(hoverTimerRef.current);
    // Ignore the ghost click/hover that lands on the preview right after closing.
    if (Date.now() - closedAtRef.current < REOPEN_GUARD_MS) return;
    setExpanded(true);
    void trackTelemetryEvent('promo_video_open', { trigger });
  }, []);

  const close = useCallback(() => {
    const video = fullVideoRef.current;
    if (video) {
      void trackTelemetryEvent('promo_video_close', { watched_seconds: Math.round(video.currentTime) });
      video.pause();
    }
    window.clearTimeout(hoverTimerRef.current);
    closedAtRef.current = Date.now();
    // The close button sits over the preview's spot; require the mouse to leave
    // the preview before hover can reopen it.
    hoverArmedRef.current = false;
    setExpanded(false);
    setNeedsTapForSound(false);
  }, []);

  const dismiss = (event) => {
    event.stopPropagation();
    setDismissed(true);
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch (e) { /* storage unavailable */ }
    void trackTelemetryEvent('promo_video_dismiss', {});
  };

  // Start the full player from the beginning whenever it opens.
  useEffect(() => {
    if (!expanded) return undefined;
    const video = fullVideoRef.current;
    if (!video) return undefined;
    video.currentTime = 0;
    video.muted = true;
    setNeedsTapForSound(true);
    void video.play().catch(() => {});

    const onKey = (event) => { if (event.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [expanded, close]);

  useEffect(() => () => window.clearTimeout(hoverTimerRef.current), []);

  // After closing, hover stays disarmed until the mouse is seen outside the
  // preview (the preview can reappear under a cursor that never "entered" it).
  useEffect(() => {
    if (expanded) return undefined;
    const onMove = (event) => {
      if (event.pointerType !== 'mouse' || hoverArmedRef.current) return;
      if (!pipRef.current || !pipRef.current.contains(event.target)) hoverArmedRef.current = true;
    };
    document.addEventListener('pointermove', onMove);
    return () => document.removeEventListener('pointermove', onMove);
  }, [expanded]);

  // Lets the page reserve room for the preview on small screens (see CSS).
  useEffect(() => {
    if (dismissed) return undefined;
    document.body.classList.add('has-promo-pip');
    return () => document.body.classList.remove('has-promo-pip');
  }, [dismissed]);

  const tryEnableSound = useCallback(() => {
    const video = fullVideoRef.current;
    if (!video || !video.muted) return;
    video.muted = false;
    const revert = () => {
      video.muted = true;
      void video.play().catch(() => {});
      setNeedsTapForSound(true);
    };
    const attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') {
      attempt.then(() => setNeedsTapForSound(false)).catch(revert);
    }
    // Some browsers pause instead of rejecting when unmuting isn't allowed.
    window.setTimeout(() => { if (video.paused && !video.ended) revert(); }, 150);
  }, []);

  const enableSound = (event) => {
    event.stopPropagation();
    tryEnableSound();
  };

  if (dismissed) return null;

  return (
    <>
      {!expanded && (
        <div
          ref={pipRef}
          className="promo-pip"
          role="button"
          tabIndex={0}
          aria-label="Play the RKube video"
          onPointerEnter={(event) => {
            if (event.pointerType !== 'mouse' || !hoverArmedRef.current) return;
            hoverTimerRef.current = window.setTimeout(() => open('hover'), HOVER_OPEN_DELAY_MS);
          }}
          onPointerLeave={() => {
            window.clearTimeout(hoverTimerRef.current);
            hoverArmedRef.current = true;
          }}
          onClick={() => open('click')}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              open('keyboard');
            }
          }}
        >
          {reducedMotion ? (
            <img src={POSTER_SRC} alt="" className="promo-pip-media" />
          ) : (
            <video
              className="promo-pip-media"
              src={PREVIEW_SRC}
              poster={POSTER_SRC}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              aria-hidden="true"
            />
          )}
          <span className="promo-pip-play" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="18" height="18"><path d="M8 5v14l11-7z" fill="currentColor" /></svg>
            Watch
          </span>
          <button type="button" className="promo-pip-dismiss" aria-label="Hide video" onClick={dismiss}>×</button>
        </div>
      )}

      {expanded && (
        <div className="promo-overlay" role="dialog" aria-modal="true" aria-label="RKube video" onClick={close}>
          <div
            className="promo-overlay-inner"
            onClick={(event) => event.stopPropagation()}
            onPointerMove={(event) => { if (event.pointerType === 'mouse') tryEnableSound(); }}
            onPointerDown={tryEnableSound}
          >
            <video
              ref={fullVideoRef}
              className="promo-overlay-video"
              src={FULL_SRC}
              poster={POSTER_SRC}
              controls
              playsInline
              preload="auto"
              onEnded={() => void trackTelemetryEvent('promo_video_complete', {})}
              onVolumeChange={(event) => setNeedsTapForSound(event.currentTarget.muted)}
            />
            {needsTapForSound && (
              <button type="button" className="promo-sound" onClick={enableSound}>
                <svg viewBox="0 0 24 24" width="18" height="18"><path d="M3 10v4h4l5 5V5L7 10H3zm13.5 2A4.5 4.5 0 0014 8v8a4.5 4.5 0 002.5-4z" fill="currentColor" /></svg>
                Tap for sound
              </button>
            )}
          </div>
          <button
            type="button"
            className="promo-overlay-close"
            aria-label="Close video"
            onClick={(event) => { event.stopPropagation(); close(); }}
          >
            ×
          </button>
        </div>
      )}
    </>
  );
}

export default PromoVideoPip;
